import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogPanel,
  Transition,
} from '@headlessui/react';
import { BreakType, DaySequence, Job } from '@core/generated/gql-types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import { formatTimeForTimePicker } from '@core/util/jobTimezone.util';
import { jobDaysOvernight } from '@core/util/jobScheduleBounds.util';
import TimePicker from '@/components/shared/TimePicker';
import SingleDatePicker from '@/components/shared/SingleDatePicker';
import {
  formatLoggedTime,
  formatTime,
  getTimestampFromTime,
  getJobEndTimeString,
} from '@/utils/utils';
import { parseScheduleDateLocal } from '@core/util/jobScheduleBounds.util';
import Button from '@/components/Button';

const DEFAULT_CLOCK_OUT_TIME = '17:00';

export type CheckoutTimesheetValues = {
  clockInTimestamp: string;
  clockOutTimestamp: string;
  breakMinutes: number;
};

interface CheckoutTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  daySequence: DaySequence | null;
  onCheckout: (values: CheckoutTimesheetValues) => Promise<void>;
}

function calendarDayFromSequenceTimestamp(
  msString?: string | null,
  fallbackDateISO?: string | null,
): Date {
  if (msString) {
    const n = Number(msString);
    if (!Number.isNaN(n)) {
      return dayjs(n).startOf('day').toDate();
    }
  }
  if (fallbackDateISO) {
    return parseScheduleDateLocal(fallbackDateISO).toDate();
  }
  return dayjs().startOf('day').toDate();
}

/**
 * Client / tenant checkout when a worker has clocked in but not out.
 * Submits via `clockAction` (see useJobTimesheets.handleCheckoutTimesheet).
 */
const CheckoutTimesheetModal = ({
  isOpen,
  onClose,
  job,
  daySequence,
  onCheckout,
}: CheckoutTimesheetModalProps) => {
  const isOvernightShift = useMemo(
    () => jobDaysOvernight(job.days),
    [job.days?.startTime, job.days?.endTime],
  );

  const getTimePickerValue = useCallback(
    (
      unixMs?: string | null,
      scheduleClockTime?: string | null,
      fallback = DEFAULT_CLOCK_OUT_TIME,
    ): string =>
      formatTimeForTimePicker(unixMs, scheduleClockTime, job, fallback),
    [job],
  );

  const [clockInDate, setClockInDate] = useState<Date>(() =>
    dayjs().startOf('day').toDate(),
  );
  const [clockOutDate, setClockOutDate] = useState<Date>(() =>
    dayjs().startOf('day').toDate(),
  );
  const [clockOutTime, setClockOutTime] = useState<string>(
    DEFAULT_CLOCK_OUT_TIME,
  );
  const [breakTimeMinutes, setBreakTimeMinutes] = useState<string>('0');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !daySequence) return;

    const fallbackISO = daySequence.startDateISO || null;
    const inMs = daySequence.clockInTime || null;
    const nextInDate = calendarDayFromSequenceTimestamp(inMs, fallbackISO);
    setClockInDate(nextInDate);

    let nextOutDate = calendarDayFromSequenceTimestamp(null, fallbackISO);
    if (isOvernightShift) {
      nextOutDate = dayjs(nextInDate).add(1, 'day').startOf('day').toDate();
    } else {
      nextOutDate = nextInDate;
    }
    if (dayjs(nextOutDate).isBefore(dayjs(nextInDate), 'day')) {
      nextOutDate = nextInDate;
    }
    setClockOutDate(nextOutDate);
    setClockOutTime(
      getTimePickerValue(
        null,
        job.days?.endTime,
        getJobEndTimeString(job) || DEFAULT_CLOCK_OUT_TIME,
      ),
    );
    setBreakTimeMinutes('0');
  }, [
    isOpen,
    daySequence,
    job,
    isOvernightShift,
    getTimePickerValue,
    job.days?.startTime,
    job.days?.endTime,
  ]);

  const jobCalendarBounds = useMemo(() => {
    const startMs = job.startDate ? Number(job.startDate) : NaN;
    const endMs = job.endDate ? Number(job.endDate) : NaN;
    const min = !Number.isNaN(startMs)
      ? dayjs(startMs).startOf('day').toDate()
      : undefined;
    const max = !Number.isNaN(endMs)
      ? dayjs(endMs).startOf('day').toDate()
      : undefined;
    return { min, max };
  }, [job.startDate, job.endDate]);

  const clockOutDateMax = useMemo(() => {
    if (!jobCalendarBounds.max) return undefined;
    return dayjs(jobCalendarBounds.max)
      .add(isOvernightShift ? 1 : 0, 'day')
      .toDate();
  }, [jobCalendarBounds.max, isOvernightShift]);

  const clockInTimestamp = useMemo(
    () => daySequence?.clockInTime ?? '',
    [daySequence?.clockInTime],
  );

  const clockOutTimestamp = useMemo(
    () =>
      getTimestampFromTime(
        dayjs(clockOutDate).format('YYYY-MM-DD'),
        clockOutTime,
      ),
    [clockOutDate, clockOutTime],
  );

  const timesheetDuration = useMemo(
    () =>
      formatLoggedTime(
        clockInTimestamp,
        clockOutTimestamp,
        minutesToMilliseconds(parseInt(breakTimeMinutes, 10) || 0),
      ),
    [clockInTimestamp, clockOutTimestamp, breakTimeMinutes],
  );

  const isInvalidTimeRange = useMemo(
    () =>
      Boolean(
        clockInTimestamp &&
        clockOutTimestamp &&
        Number(clockOutTimestamp) <= Number(clockInTimestamp),
      ),
    [clockInTimestamp, clockOutTimestamp],
  );

  const invalidInput = useMemo(
    () => isInvalidTimeRange || timesheetDuration === '0 hrs',
    [isInvalidTimeRange, timesheetDuration],
  );

  const onSubmit = async () => {
    if (isSubmitting || invalidInput || !daySequence?.clockInTime) return;

    setIsSubmitting(true);
    try {
      await onCheckout({
        clockInTimestamp: daySequence.clockInTime,
        clockOutTimestamp,
        breakMinutes: parseInt(breakTimeMinutes, 10) || 0,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!daySequence) {
    return null;
  }

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-y-auto rounded-2xl bg-white px-6 py-12 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="flex items-center justify-center pb-5 text-lg leading-6 font-medium text-gray-900"
                >
                  <span className="py-2 text-lg font-medium text-gray-900">
                    Check-out
                  </span>
                </DialogTitle>

                <div className="flex flex-col space-y-8">
                  <p className="text-center text-sm text-gray-500">
                    The candidate has checked in. Record a check-out time to
                    propose closing the shift.
                  </p>

                  <div className="flex items-center justify-between">
                    <span>Proposed logged time</span>
                    <span className="font-medium text-orange-500">
                      {clockInTimestamp && clockOutTimestamp
                        ? `${formatTime(clockInTimestamp, job)} - ${formatTime(clockOutTimestamp, job)} (${timesheetDuration})`
                        : '—'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="shrink-0">Clock-in date</span>
                        <span className="text-sm font-medium text-gray-900">
                          {dayjs(clockInDate).format('D MMM YYYY')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Clock-in time</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatTime(clockInTimestamp, job)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="shrink-0">Clock-out date</span>
                        <SingleDatePicker
                          value={clockOutDate}
                          onChange={setClockOutDate}
                          minDate={dayjs(clockInDate).startOf('day').toDate()}
                          maxDate={clockOutDateMax}
                          isInvalid={isInvalidTimeRange}
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Clock-out time</span>
                        <div className="flex-1">
                          <TimePicker
                            value={clockOutTime}
                            onChange={setClockOutTime}
                            label=""
                            onBlur={() => {}}
                            isInvalid={isInvalidTimeRange}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-between gap-2">
                    <span>Break time (minutes)</span>
                    <div className="min-w-60">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={breakTimeMinutes}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === '' || value === '-') {
                            setBreakTimeMinutes('');
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!Number.isNaN(numValue) && numValue >= 0) {
                              setBreakTimeMinutes(value);
                            }
                          }
                        }}
                        onBlur={(event) => {
                          if (
                            event.target.value === '' ||
                            Number.isNaN(parseInt(event.target.value, 10))
                          ) {
                            setBreakTimeMinutes('0');
                          }
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={onSubmit}
                      disabled={isSubmitting || invalidInput}
                      isLoading={isSubmitting}
                      className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                    >
                      {isSubmitting ? 'Submitting…' : 'Check-out'}
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CheckoutTimesheetModal;
